import { inject, Service } from "@angular/core";
import { NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";

import { LanguageService } from "./language-service";

/**
 * Drop-in replacement for `NgbModal` that stops modal dismissals from surfacing as
 * unhandled promise rejections.
 *
 * `NgbModalRef.result` rejects when a modal is dismissed rather than closed, and the
 * rejection value is the dismiss reason: `0` for a backdrop click, `1` for Escape, or
 * whatever was passed to `dismiss()`. Call sites consume it as `modal.result.then(cb)`,
 * which leaves the derived promise rejected and unhandled, so every dismissal reaches
 * the global `ErrorHandler` and is reported as a crash.
 *
 * Here `result` resolves with `undefined` on dismissal instead, which is the value
 * call sites already treat as "the user cancelled".
 *
 * It also applies the language direction class every modal needs, so call sites no
 * longer have to pass `modalDialogClass` themselves.
 */
@Service()
export class AdaaModalService extends NgbModal {
  private readonly _languageService = inject(LanguageService);

  public override open(content: unknown, options?: NgbModalOptions): NgbModalRef {
    const modalRef = super.open(content, this._withDirectionClass(options));
    const dismissAsUndefined = modalRef.result.catch(() => undefined);

    Object.defineProperty(modalRef, "result", {
      configurable: true,
      get: () => dismissAsUndefined,
    });

    return modalRef;
  }

  /**
   * Forces the modal onto the current language direction, keeping any other classes
   * the caller asked for. A direction the caller passed is dropped rather than merged,
   * so a stale `modal-rtl`/`modal-ltr` can never fight the active language.
   */
  private _withDirectionClass(options?: NgbModalOptions): NgbModalOptions {
    const callerClasses = (options?.modalDialogClass ?? "")
      .split(" ")
      .filter((token) => token && token !== "modal-rtl" && token !== "modal-ltr");

    return {
      ...options,
      modalDialogClass: [...callerClasses, this._languageService.modalDirection()].join(" "),
    };
  }
}
